import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react/react/docs/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import State from '../../components/State';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import {Dialog} from '@primer/react/drafts'
`}</code></pre>

    <p>{`The dialog component is the Primer implementation of the ARIA design pattern `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/wai-aria-practices-1.1/#dialog_modal"
      }}>{`Dialog`}</a>{`. A dialog is a type of overlay that can be used for confirming actions, asking for disambiguation, and presenting small forms. They generally allow the user to focus on a quick task without having to navigate to a different page.`}</p>
    <p><strong parentName="p">{`Dialogs appear in the page after a direct user interaction`}</strong>{`. Don't show dialogs on page load or as system alerts.`}</p>
    <p><strong parentName="p">{`Dialogs appear centered in the page`}</strong>{`, with a visible backdrop that dims the rest of the window for focus.`}</p>
    <p><strong parentName="p">{`All dialogs should have a title and a close button`}</strong>{`. Use the `}<inlineCode parentName="p">{`title`}</inlineCode>{` prop to set the title. The close button is created automatically, but must be handled with an `}<inlineCode parentName="p">{`onClose`}</inlineCode>{` prop.`}</p>
    <p><strong parentName="p">{`Dialogs are modal`}</strong>{`. Dialogs can be dismissed by clicking on the close button, by interacting with another button in the overlay, or by clicking outside of the overlay.`}</p>
    <p><strong parentName="p">{`(Coming soon) Make sure larger dialogs remain mobile-friendly`}</strong>{`. Even large dialogs need to be responsive. A dialog's width and height will be readjusted depending on the screen size and should never exceed the available space. Use responsive solutions to adjust the UI so they behave well on smaller screens.`}</p>
    <p><strong parentName="p">{`(Coming soon) Simple and small dialogs can remain as-is on mobile`}</strong>{`. As more elements are added to it, mobile-specific style variations such as `}<strong parentName="p">{`Bottom sheet`}</strong>{` and `}<strong parentName="p">{`Full-screen`}</strong>{` should be considered.`}</p>
    <h3>{`State`}</h3>
    <p>{`The dialog component is completely stateless. The parent component must conditionally render a dialog based on the necessary criteria. The parent component can be notified by a gesture to close the dialog (e.g. by clicking the "X" button or by pressing the Escape key) by passing in the `}<inlineCode parentName="p">{`onClose`}</inlineCode>{` prop.`}</p>
    <h3>{`Accessibility`}</h3>
    <p>{`The dialog component is fully accessible out-of-the-box. The dialog's title is used for `}<inlineCode parentName="p">{`aria-labelledby`}</inlineCode>{`, and the dialog's description is used for `}<inlineCode parentName="p">{`aria-describedby`}</inlineCode>{`. The `}<inlineCode parentName="p">{`aria-modal="true"`}</inlineCode>{` attribute is used to inform screen readers that the rest of the content on the page is inert.`}</p>
    <h4>{`Keyboard`}</h4>
    <p>{`The default keyboard API for a dialog employs three mechanisms:`}</p>
    <ol>
      <li parentName="ol">{`The Escape key can be pressed to close the dialog.`}</li>
      <li parentName="ol">{`Focus is trapped within the top-most dialog. When a dialog is opened, the close button receives initial focus by default, or on a button defined with `}<inlineCode parentName="li">{`autoFocus: true`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`If there are buttons in the dialog footer, focus can be moved between them with left and right arrow keys or tab/shift+tab.`}</li>
      <li parentName="ol">{`When a dialog is closed, it can optionally handle returning focus to the element that was focused immediately before the dialog was opened. Otherwise, it is the consumer's responsibility to move focus to a suitable element.`}</li>
    </ol>
    <h3>{`Custom rendering`}</h3>
    <p><strong parentName="p">{`Note: using custom rendering allows breaking out of the defined design, UX, and accessibility patterns of the dialog. Use only as a last resort.`}</strong></p>
    <p>{`By default, the Dialog component implements the design and interactions defined by the Primer design system. If necessary, you can provide custom renderers for the header, body, or footer using the `}<inlineCode parentName="p">{`renderHeader`}</inlineCode>{`, `}<inlineCode parentName="p">{`renderBody`}</inlineCode>{`, and `}<inlineCode parentName="p">{`renderFooter`}</inlineCode>{` props, respectively. The JSX produced by these render props will render full-bleed into their respective areas of the dialog. If you are using the custom renderers, you should use the provided sub-components `}<inlineCode parentName="p">{`Dialog.Header`}</inlineCode>{`, `}<inlineCode parentName="p">{`Dialog.Title`}</inlineCode>{`, `}<inlineCode parentName="p">{`Dialog.Subtitle`}</inlineCode>{`, `}<inlineCode parentName="p">{`Dialog.CloseButton`}</inlineCode>{`, `}<inlineCode parentName="p">{`Dialog.Body`}</inlineCode>{`, `}<inlineCode parentName="p">{`Dialog.Footer`}</inlineCode>{`, and `}<inlineCode parentName="p">{`Dialog.Buttons`}</inlineCode>{` to the extent possible.`}</p>
    <h3>{`Example`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live drafts",
        "live": true,
        "drafts": true
      }}>{`<State default={false}>
  {([isOpen, setIsOpen]) => {
    const openDialog = React.useCallback(() => setIsOpen(true), [setIsOpen])
    const closeDialog = React.useCallback(() => setIsOpen(false), [setIsOpen])
    return (
      <>
        <Button onClick={openDialog}>Open</Button>
        {isOpen && (
          <Dialog
            title="Dialog example"
            subtitle={
              <>
                This is a <b>description</b> of the dialog.
              </>
            }
            footerButtons={[{content: 'Ok', onClick: closeDialog}]}
            onClose={closeDialog}
          >
            <Text fontFamily="sans-serif">Some content</Text>
          </Dialog>
        )}
      </>
    )
  }}
</State>
`}</code></pre>
    <h2>{`Component props`}</h2>
    <h3>{`DialogProps`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Prop name`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`title`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"Dialog"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Sets the title of the dialog, which by default is also used as the `}<inlineCode parentName="td">{`aria-labelledby`}</inlineCode>{` attribute.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`subtitle`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Optional. Sets the subtitle of the dialog, which by default is also used as the `}<inlineCode parentName="td">{`aria-describedby`}</inlineCode>{` attribute.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`renderHeader`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`(props: DialogHeaderProps) => JSX.Element`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Optional. Custom render the dialog header. See "Custom rendering" above for more info.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`renderBody`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`(props: DialogProps) => JSX.Element`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Optional. Custom render the dialog body. See "Custom rendering" above for more info.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`renderFooter`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`(props: DialogProps) => JSX.Element`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Optional. Custom render the dialog footer. See "Custom rendering" above for more info.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`footerButtons`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`DialogButtonProps[]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Optional. Specify buttons that will be rendered in the footer of the dialog.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`onClose`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`(gesture: 'close-button' │ 'escape') => void`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Required. This method is invoked when a gesture to close the dialog is used (either an Escape key press or clicking the "X" in the top-right corner). The gesture argument indicates the gesture that was used to close the dialog (either 'close-button' or 'escape').`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`role`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"dialog" │ "alertdialog"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"dialog"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The ARIA role given to the dialog element. More info: `}<a parentName="td" {...{
              "href": "https://www.w3.org/TR/wai-aria-practices-1.1/#dialog_modal"
            }}>{`dialog`}</a>{`, `}<a parentName="td" {...{
              "href": "https://www.w3.org/TR/wai-aria-practices-1.1/#alertdialog"
            }}>{`alertdialog`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`width`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"small" │ "medium" │ "large" │ "xlarge"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"xlarge"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The fixed width of the dialog.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`height`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"small" │ "large" │ "auto"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"auto"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The fixed height of the dialog, or, auto to adjust the height based on its contents.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`sx`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`SystemStyleObject`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`{}`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Style to be applied to the component`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`DialogHeaderProps`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`DialogHeaderProps`}</inlineCode>{` interface extends `}<inlineCode parentName="p">{`DialogProps`}</inlineCode>{` and adds these additional properties:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Prop name`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`dialogLabelId`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`ID of the element that will be used as the `}<inlineCode parentName="td">{`aria-labelledby`}</inlineCode>{` attribute on the dialog. This ID should be set to the element that renders the dialog's title.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`dialogDescriptionId`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`ID of the element that will be used as the `}<inlineCode parentName="td">{`aria-describedby`}</inlineCode>{` attribute on the dialog. This ID should be set to the element that renders the dialog's subtitle.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`DialogButtonProps`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`DialogButtonProps`}</inlineCode>{` interface extends `}<inlineCode parentName="p">{`ButtonProps`}</inlineCode>{` (see Button) and adds these additional properties:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Prop name`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`buttonType`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"normal" │ "primary" │ "danger"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`Button`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The type of button to render`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`content`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Required. The button's inner content.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`autoFocus`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`false`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`If true, this button will be automatically focused when the dialog is first rendered. If `}<inlineCode parentName="td">{`autoFocus`}</inlineCode>{` is true on subsequent button definitions, it will be ignored.`}</td>
        </tr>
      </tbody>
    </table>
    <h2>{`ConfirmationDialog`}</h2>
    <p>{`A `}<inlineCode parentName="p">{`ConfirmationDialog`}</inlineCode>{` is a special kind of dialog with more rigid behavior. It is used to confirm a user action. `}<inlineCode parentName="p">{`ConfirmationDialog`}</inlineCode>{`s always have exactly two buttons: one to cancel the action and one to confirm it. No custom rendering capabilities are provided for ConfirmationDialog.`}</p>
    <h3>{`useConfirm hook`}</h3>
    <p>{`An alternate way to use `}<inlineCode parentName="p">{`ConfirmationDialog`}</inlineCode>{` is with the `}<inlineCode parentName="p">{`useConfirm()`}</inlineCode>{` hook. It takes no parameters and returns an `}<inlineCode parentName="p">{`async`}</inlineCode>{` function, `}<inlineCode parentName="p">{`confirm`}</inlineCode>{`. When `}<inlineCode parentName="p">{`confirm`}</inlineCode>{` is called (see ConfirmOptions below for its argument), it shows the confirmation dialog. When the dialog is dismissed, a promise is resolved with `}<inlineCode parentName="p">{`true`}</inlineCode>{` or `}<inlineCode parentName="p">{`false`}</inlineCode>{` depending on whether or not the confirm button was used.`}</p>
    <h3>{`Example (with `}<inlineCode parentName="h3">{`useConfirm`}</inlineCode>{` hook)`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`function ShorthandExample2() {
  const confirm = useConfirm()
  const buttonClick = React.useCallback(
    async function (e) {
      if (await confirm({title: 'Are you sure?', content: 'You must confirm this action to continue.'})) {
        e.target.textContent = 'Confirmed!'
      }
    },
    [confirm]
  )
  return (
    <>
      <Button onClick={buttonClick}>Confirmable action</Button>
    </>
  )
}
render(<ShorthandExample2 />)
`}</code></pre>
    <h3>{`Example (using the full `}<inlineCode parentName="h3">{`ConfirmationDialog`}</inlineCode>{` component)`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<State default={false}>
  {([isOpen, setIsOpen]) => {
    const openDialog = React.useCallback(() => setIsOpen(true), [setIsOpen])
    const closeDialog = React.useCallback(() => setIsOpen(false), [setIsOpen])
    return (
      <>
        <Button onClick={openDialog}>Open</Button>
        {isOpen && (
          <ConfirmationDialog title="Confirm action?" onClose={closeDialog}>
            Are you sure you want to confirm this action?
          </ConfirmationDialog>
        )}
      </>
    )
  }}
</State>
`}</code></pre>
    <h3>{`ConfirmationDialogProps`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Prop name`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`title`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Required. Sets the title of the dialog, which by default is also used as the `}<inlineCode parentName="td">{`aria-labelledby`}</inlineCode>{` attribute.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`onClose`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`(gesture: 'confirm' │ 'cancel' │ 'close-button' │ 'escape') => void`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Required. This callback is invoked when a gesture to close the dialog is performed. The first argument indicates the gesture.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`cancelButtonContent`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"Cancel"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The content to use for the cancel button.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`confirmButtonContent`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"OK"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The content to use for the confirm button.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`confirmButtonType`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"normal" │ "primary" │ "danger"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`Button`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The type of button to use for the confirm button.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`ConfirmOptions`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Prop name`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`title`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Required. Sets the title of the dialog, which by default is also used as the `}<inlineCode parentName="td">{`aria-labelledby`}</inlineCode>{` attribute.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`content`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Required. Used as the body of the ConfirmationDialog that is displayed.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`cancelButtonContent`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"Cancel"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The content to use for the cancel button.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`confirmButtonContent`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"OK"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The content to use for the confirm button.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`confirmButtonType`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`"normal" │ "primary" │ "danger"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`Button`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The type of button to use for the confirm button.`}</td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      